// Service worker dead-man's-switch (2026-08-09, Z-campaign Phase 1,
// Defect B) — a repeatedly-crashing or wedged app can never complete the
// three-gate update flow (waiting -> user's reload click -> skipWaiting ->
// controlling), so a bad deploy could permanently brick installed apps
// until a manual delete-and-reinstall. This module tracks boot frequency
// and whether any boot has actually settled, and hands back a pure
// yes/no decision — it never touches the ServiceWorkerRegistration or SW
// APIs itself (registerServiceWorker.js owns that), so it stays fully
// unit-testable without a real service worker.
//
// Deliberately NOT built on authTrace.js's debug-gated machinery: this has
// to keep working even if that debug scaffolding is stripped, broken, or
// mid-refactor (Phase 2 of this same campaign prunes it) — a bricked app
// is exactly the scenario debug tooling is least likely to be reachable
// in. Plain localStorage, always on, no debugEnabled gate anywhere here.
//
// Phase 2 boundary (2026-08-09, explicit per review) — this file is
// PERMANENT production safety code, not campaign debug scaffolding, and
// Phase 2's scaffolding prune does not gate, strip, or debug-condition
// ANY of it. Every export below (recordBoot, markBootHealthy,
// readWatchdogState, shouldForceUpdate, markUpdateFired,
// isCallbackLanding) stays exactly as unconditional as it already was.
// The only debug-gated things anywhere in this mechanism are the two
// trace('sw-watchdog-...', ...) calls in registerServiceWorker.js, which
// were always just observability on top of the (always-active) decision —
// removing them would not change what the watchdog does, only whether you
// can see it doing it on a non-prod host.

const BOOT_WINDOW_KEY = 'rosteremSwWatchdogBoots'; // JSON array of boot timestamps
const HEALTHY_KEY = 'rosteremSwWatchdogHealthyAt'; // timestamp of the last settled boot
const FIRED_KEY = 'rosteremSwWatchdogFiredAt'; // timestamp of the last forced-update attempt

export const BOOT_WINDOW_MS = 2 * 60 * 1000; // 2 minutes
export const BOOT_THRESHOLD = 3;
// One forced attempt per burst: once fired, stays silent for a full window
// even if boots keep piling up, so a genuinely wedged app doesn't spam
// registration.update() on every subsequent boot.
export const REFIRE_COOLDOWN_MS = BOOT_WINDOW_MS;

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Storage full/unavailable — the watchdog degrades to a permanent
    // no-op (bootCount always reads 0) rather than throwing past this.
  }
}

// Called once per app boot, as early as possible (main.js) — before
// anything that could crash has a chance to. Prunes anything outside the
// rolling window before appending, so a burst from hours/days ago can
// never combine with today's.
export function recordBoot(now = Date.now()) {
  const boots = readJSON(BOOT_WINDOW_KEY, []).filter((ts) => now - ts <= BOOT_WINDOW_MS);
  boots.push(now);
  writeJSON(BOOT_WINDOW_KEY, boots);
  return boots.length;
}

// Called once a navigation has resolved AND stayed quiet for the settle
// window (router/index.js's nav-resolved hook) — the actual signature of a
// landed boot, not a looping one. Clears the boot-window history: a
// healthy landing means this boot didn't contribute to a crash-loop, so it
// shouldn't be able to combine with a future unrelated burst either.
export function markBootHealthy(now = Date.now()) {
  writeJSON(HEALTHY_KEY, now);
  writeJSON(BOOT_WINDOW_KEY, []);
}

export function readWatchdogState(now = Date.now()) {
  const boots = readJSON(BOOT_WINDOW_KEY, []).filter((ts) => now - ts <= BOOT_WINDOW_MS);
  return {
    bootCount: boots.length,
    lastHealthyAt: readJSON(HEALTHY_KEY, null),
    lastFiredAt: readJSON(FIRED_KEY, null),
  };
}

export function markUpdateFired(now = Date.now()) {
  writeJSON(FIRED_KEY, now);
}

// Pure decision — explicit inputs, no localStorage reads of its own, so
// the actual trigger logic is testable without mocking storage at all.
export function shouldForceUpdate({ bootCount, lastHealthyAt, lastFiredAt, now }) {
  if (bootCount < BOOT_THRESHOLD) return false;
  // Belt-and-braces: markBootHealthy() already clears the boot window, so
  // bootCount alone should already be < BOOT_THRESHOLD after a healthy
  // landing. Kept as an explicit second guard in case that clearing
  // behaviour is ever changed without updating this function too.
  if (lastHealthyAt !== null && now - lastHealthyAt < BOOT_WINDOW_MS) return false;
  if (lastFiredAt !== null && now - lastFiredAt < REFIRE_COOLDOWN_MS) return false;
  return true;
}

// An Auth0 redirect-callback landing (?code=&state=) must never be
// interrupted by a forced update — see registerServiceWorker.js's use of
// this guard. Exported as its own predicate (same idiom as
// resetOrgResolutionOnFreshLogin.js's callback detection) so it gets a
// direct test rather than only a source-text regex on the call site.
export function isCallbackLanding(search) {
  return typeof search === 'string' && search.includes('code=');
}
